import { styled, css } from "styled-components";
import { Link } from "@ludens-reklame/rubics-v3-sdk/link/types";
import { mobileBreakPointCSS } from "../defaults/theme.js";
import { buttonReset, cssFontSize, cssSpacing } from "../util/styles.js";
import { useLinkProps } from "../hooks/useLinkProps.js";
import { arrow } from "../icons/index.js";

export interface ButtonProps {
  $variant?: "primary" | "secondary" | "tertiary" | "outlined";
  $animation?: boolean;
}

const buttonCss = css<ButtonProps>`
  display: inline-flex;
  box-sizing: border-box;
  height: ${cssSpacing("l")};
  padding: ${cssSpacing("xs")} ${cssSpacing("m")};
  justify-content: center;
  align-items: center;
  color: var(--primary-button-color);
  background-color: var(--primary-button-background);
  border: 1px solid var(--primary-button-background);
  border-radius: 100px;
  cursor: pointer;
  font-size: ${cssFontSize("small")};
  line-height: 24px;
  text-decoration: none;
  font-weight: 400;
  transition: transform 0.12s ease-in-out;
  white-space: pre-wrap;

  @media (min-width: ${mobileBreakPointCSS}) {
    font-size: 1.125rem;
  }

  &:hover,
  &:focus {
    transform: scale(1.03);
  }

  .icon {
    display: flex;
    align-items: center;
    line-height: 0;
    margin-left: ${(props) => props.theme.spacing.xs};

    svg {
      width: 20px;
      height: 20px;
      fill: currentColor;

      path {
        fill: currentColor;
      }
    }
  }

  &.animate {
    transition: opacity 0.3s linear 0s;

    &:hover {
      transform: scale(1);

      i {
        opacity: 1;
        right: 0;
        width: 21px;
        margin-left: ${(props) => props.theme.spacing.s};
      }
    }

    span {
      left: 0;
      position: relative;
      transition: all 0.2s ease-out 0s;
    }

    i {
      display: flex;
      width: 0;
      position: relative;
      right: 21px;
      opacity: 0;
      transition: all 0.2s ease-out 0s;
      line-height: 1.5;
      margin: 0;
    }
  }

  ${(props) =>
    props.$variant === "secondary" &&
    css`
      color: var(--secondary-button-color);
      background-color: var(--secondary-button-background);
      border-color: var(--secondary-button-background);
    `}

  ${(props) =>
    props.$variant === "outlined" &&
    css`
      color: var(--outline-button-color);
      background-color: var(--outline-button-background);
      border-color: var(--outline-button-border);
    `}

  ${(props) =>
    props.$variant === "tertiary" &&
    css`
      color: var(--tertiary-button-color);
      background-color: var(--tertiary-button-background);
      border-color: var(--tertiary-button-background);
    `}
`;

export const Button = styled.button<ButtonProps>`
  ${buttonReset};
  ${buttonCss}
`;

export const ButtonActions = styled.div<{
  $center?: boolean;
  $gutterTop?: boolean;
  $gutterBottom?: boolean;
  $padding?: boolean;
}>`
  display: flex;
  flex-wrap: wrap;
  gap: ${cssSpacing("s")};
  flex-direction: column;
  align-items: center;

  ${(props) =>
    props.$center &&
    css`
      margin: auto;
    `}

  ${(props) =>
    props.$gutterTop &&
    css`
      margin-top: ${cssSpacing("m")};
    `}
    
  ${(props) =>
    props.$gutterBottom &&
    css`
      margin-bottom: ${cssSpacing("m")};
    `} 

  ${(props) =>
    props.$padding &&
    css`
      padding: ${cssSpacing("m")};
    `}


  @media (min-width: ${mobileBreakPointCSS}) {
    gap: ${cssSpacing("m")};
    flex-direction: row;
  }
`;

export interface ButtonActionProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  link?: Link;
  variant?: "primary" | "secondary" | "tertiary" | "outlined";
  animation?: boolean;
  icon?: React.ReactNode;
}

export function ButtonAction({
  link,
  animation = false,
  variant = "primary",
  icon = null,
  children,
}: ButtonActionProps) {
  const linkProps = useLinkProps(link);

  const className = `button-${variant || "primary"} ${
    animation ? "animate" : ""
  }`;

  return (
    <Button
      as="a"
      className={className}
      {...linkProps}
      $variant={variant}
      $animation={animation}
    >
      <span>{children || link?.title}</span>{" "}
      {animation && <i className="icon">{arrow}</i>}
      {icon && <span className="icon">{icon}</span>}
    </Button>
  );
}
